import { authService } from '../../../services/auth.ts';
import { injectAesthetic } from '../../../utils/shared-styles.ts';
import '../../../components/library/ui/app-button.ts';

export class AppLogin extends HTMLElement {
  private email = '';
  private isProcessing = false;
  private emailSent = false;
  private showEmailFallback = false;

  static get observedAttributes() {
    return ['email'];
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (name === 'email' && newVal) {
      this.email = newVal;
      const input = this.querySelector('input[type="email"]') as HTMLInputElement;
      if (input) input.value = newVal;
    }
  }

  constructor() {
    super();
  }

  connectedCallback() {
    // Add global style for filter
    if (!document.getElementById('crate-aesthetic')) {
      const container = document.createElement('div');
      container.id = 'crate-aesthetic';
      container.style.display = 'none';
      container.innerHTML = injectAesthetic();
      document.head.appendChild(container);
    }
    
    this.render();
    this.setupListeners();
  }

  private updateState() {
    const input = this.querySelector('input[type="email"]') as HTMLInputElement;
    const button = this.querySelector('app-button');
    
    if (input) input.disabled = this.isProcessing;
    if (button) {
      if (this.isProcessing) {
          button.setAttribute('loading', '');
      } else {
          button.removeAttribute('loading');
      }
      
      if (this.isProcessing || this.emailSent) {
          button.setAttribute('disabled', '');
      } else {
          button.removeAttribute('disabled');
      }
    }

    const fallback = this.querySelector('#email-fallback-container');
    if (fallback) {
      if (this.showEmailFallback && !fallback.hasChildNodes()) {
        const btn = document.createElement('button');
        btn.className = 'email-link';
        btn.id = 'email-fallback';
        btn.textContent = 'Send me an email login link instead';
        fallback.appendChild(btn);
      } else if (!this.showEmailFallback) {
        fallback.innerHTML = '';
      }
    }
  }

  private async handleCheckAccount() {
    if (!this.email || this.isProcessing) return;

    this.isProcessing = true;
    this.updateState();

    try {
      // PRIORITY: Call signinWithAlias as soon as possible to preserve user activation.
      console.debug('>>> AppLogin: Attempting passkey sign-in for', this.email);
      try {
        const result = await authService.signinWithAlias(this.email);
        if (result) {
          console.debug('>>> AppLogin: Sign-in successful');
          return; // Success!
        }
      } catch (e: any) {
        // Now that the immediate gesture-sensitive part is over (or failed), 
        // we can update the UI state.
        this.updateState();
        
        const errorMsg = e.message?.toLowerCase() || '';
        console.warn('>>> AppLogin: Passkey sign-in failed or user not found:', errorMsg);
        
        // If it's a gesture error, we might still be in trouble, but let's try fallback.
        if (errorMsg.includes('not allowed') || errorMsg.includes('timed out')) {
          this.showEmailFallback = true;
          this.updateState();
        }

        const { exists } = await authService.checkAccount(this.email);
        if (!exists) {
          console.debug('>>> AppLogin: User does not exist, triggering registration');
          await authService.triggerEmailVerification(this.email);
          this.emailSent = true;
          this.updateState();
        } else {
          // User exists but passkey failed (maybe no keys on this device)
          this.showEmailFallback = true;
          this.updateState();
        }
      }
    } catch (e: any) {
      console.error('>>> AppLogin: Unexpected error during login flow:', e);
    } finally {
      this.isProcessing = false;
      this.updateState();
    }
  }

  private async handleEmailFallback() {
    if (!this.email || this.isProcessing) return;
    this.isProcessing = true;
    this.updateState();

    try {
      await authService.triggerEmailVerification(this.email);
      this.emailSent = true;
    } catch (e: any) {
      console.error('Failed to send email link', e);
    } finally {
      this.isProcessing = false;
      this.updateState();
    }
  }

  private setupListeners() {
    this.addEventListener('submit', (e) => {
      const target = e.target as HTMLElement;
      if (target.id === 'auth-form') {
        e.preventDefault();
        this.handleCheckAccount();
      }
    });

    this.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.type === 'email') {
        this.email = target.value;
        if (this.emailSent) {
          this.emailSent = false;
          this.updateState();
        }
      }
    });

    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.id === 'email-fallback') {
        this.handleEmailFallback();
      }
    });
  }

  private render() {
    this.innerHTML = `
      <style>
        .login-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100vw;
          height: 100vh;
          background-color: var(--surface);
          color: var(--text-color);
          font-family: var(--font-family);
          position: relative;
        }

        .login-container {
          background: rgba(20, 20, 20, 0.4);
          backdrop-filter: blur(16px);
          padding: 40px;
          width: 100%;
          max-width: 450px;
          position: relative;
          z-index: 10;
        }

        .login-container::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          filter: url(#sketchy);
          pointer-events: none;
          z-index: -1;
        }

        .header {
          margin-bottom: 32px;
        }

        .logo-container {
          margin-bottom: 16px;
        }

        h1 {
          margin-top: 0;
          font-size: 28px;
          margin-bottom: 8px;
          font-weight: 600;
          letter-spacing: 0.05em;
          color: white;
        }

        p {
          color: var(--text-dim);
          font-size: 14px;
          margin: 0;
        }

        .input-group {
          margin-bottom: 24px;
          text-align: left;
        }

        label {
          display: block;
          margin-bottom: 8px;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.6;
        }

        input {
          width: 100%;
          padding: 12px 16px;
          box-sizing: border-box;
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          color: white;
          font-family: inherit;
          font-size: 16px;
          outline: none;
          transition: all 0.2s ease;
        }

        input:focus {
          border-color: var(--accent);
          background: rgba(0, 0, 0, 0.4);
        }

        .email-link {
          margin-top: 24px;
          color: var(--text-dim);
          font-size: 13px;
          background: none;
          border: none;
          text-decoration: underline;
          cursor: pointer;
          transition: color 0.2s;
        }

        .email-link:hover {
          color: white;
        }

        .footer-text {
          margin-top: 32px;
          font-size: 11px;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.5;
        }
        
        .doodle-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
      </style>

      <div class="login-wrapper drafting-grid">
        <div class="doodle-bg">
           <div class="doodle doodle-lg doodle-animated" style="top: 12%; right: 8%; opacity: 0.14;">
              <svg viewBox="0 0 80 80" fill="none">
                <path d="M15 30L15 65L65 65L65 30L15 30Z" stroke="#f59e0b" stroke-width="2" style="filter: url(#sketchy);" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M17 42L63 42" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"></path>
              </svg>
           </div>
        </div>

        <div class="login-container">
          <div class="header">
            <div class="logo-container">
              <img src="/images/crate_sketch.png" alt="Crate" style="height: 60px;" />
            </div>
            <h1>Crate</h1>
            <p>Access the Network</p>
          </div>

          <form id="auth-form">
            <div class="input-group">
              <label>Email address</label>
              <input type="email" placeholder="name@company.com" value="${this.email}" required />
            </div>
            <app-button variant="primary" size="sm" type="submit" block>
              ${this.emailSent ? 'CHECK EMAIL' : 'CONTINUE'}
            </app-button>
          </form>

          <div id="email-fallback-container"></div>

          <div class="footer-text">
            Protected by Biometric Passkeys
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('app-login')) {
  customElements.define('app-login', AppLogin);
}
